import { useTranslation } from "@localization/useTranslation";
import { TableFields } from "@common/components/tableDisplayFields/TableFields.types";
import { LegalEntityFieldGrouping } from "@common/components/filterPopover/FilterPopover.types";
import { useLegalEntityCustomInputFields } from "@common/hooks/useSelectableItemQueries";
import { usePlanContext } from "../../../auth/planProvider/PlanProvider";
import { useUserContext } from "../../../auth/userContextProvider/UserContextProvider";
import { usePermissionsProvider } from "../../../auth/usePermissionsProvider";
import { getLegalEntityFilterIdByColumn, LegalEntityColumnId } from "../legalEntityColumns";
import { FieldValue } from "@common/components/overviewStepSection/OverviewStepSection.types";
import { legalEntityColumnGrouping } from "../legalEntityFieldGroupings";
import { LegalEntityScope } from "../LegalEntities.types";

export interface LegalEntityFieldDefinition {
    translation: string;
    // Drawn as an information icon beside the column header, for the columns whose value follows a rule the
    // header alone does not say out loud.
    tooltip?: string;
}

// The overview's step tabs label a cell by the same name its column carries in the list, so the two read the
// same. A cell names its column with the numeric enum, which the reverse map turns back into a dictionary key.
export function getLegalEntityFieldDisplayName(field: FieldValue, dictionary: Record<string, LegalEntityFieldDefinition>): string {
    if (field.isCustomField && field.customFieldId) {
        return dictionary[field.customFieldId]?.translation ?? field.customFieldId;
    }

    if (field.column === undefined) {
        return "";
    }

    const filterId = getLegalEntityFilterIdByColumn(field.column);
    return filterId ? (dictionary[filterId]?.translation ?? filterId) : "";
}

// Every column is rendered and sorted in SQL, so the only thing decided here is which columns this user may
// pick and what they are called. Kept in step with GetDefaultColumns in LegalEntitySavedViewStrategyBase.
export default function useLegalEntityDisplayFields(scope: LegalEntityScope) {
    const { translateString, translatePropertyName } = useTranslation();
    const { platformFeatures, vendorManagementFeatures, informationSecurityFeatures } = usePlanContext();
    const { permissions } = useUserContext();
    const { hasEnterpriseRiskManagementAccess } = usePermissionsProvider();
    const customInputFieldsQuery = useLegalEntityCustomInputFields();

    const isGroupEntities = scope === LegalEntityScope.GroupEntity;
    const hasTagsAccess = platformFeatures.tags && permissions.canAccessTags;
    const hasVendorManagement = vendorManagementFeatures.hasVendorManagement;
    const hasCertifications = vendorManagementFeatures.certifications || informationSecurityFeatures.certifications;

    const columns: Array<{ id: string } & LegalEntityFieldDefinition> = [
        { id: LegalEntityColumnId.userFriendlyId, translation: translateString("id") },
        { id: LegalEntityColumnId.name, translation: translateString("name") },
        { id: LegalEntityColumnId.country, translation: translateString("country") },
        { id: LegalEntityColumnId.businessAreas, translation: translateString("businessArea") },
        { id: LegalEntityColumnId.responsible, translation: translateString("responsible") },
        { id: LegalEntityColumnId.assets, translation: translateString("asset") },
        { id: LegalEntityColumnId.approved, translation: translateString("approved") },
        { id: LegalEntityColumnId.status, translation: translateString("status") },
        { id: LegalEntityColumnId.types, translation: translateString("type") },
    ];

    if (hasVendorManagement) {
        columns.push({ id: LegalEntityColumnId.complianceAreas, translation: translateString("complianceArea") });
        // The singular of "Klassificeringer" lives on propertyNames.Classification; the top level "classification"
        // key is the other Danish word, Klassifikation.
        columns.push({ id: LegalEntityColumnId.classifications, translation: translatePropertyName("Classification") });

        if (hasCertifications) {
            columns.push({ id: LegalEntityColumnId.certifications, translation: translateString("certification") });
        }

        if (!isGroupEntities) {
            columns.push({ id: LegalEntityColumnId.latestAudit, translation: translateString("legalEntitiesLatestAuditHeader") });
            columns.push({ id: LegalEntityColumnId.latestAuditStatus, translation: translateString("latestAuditStatus") });
        }
    }

    if (hasEnterpriseRiskManagementAccess) {
        columns.push({ id: LegalEntityColumnId.riskAreas, translation: translateString("risk") });
    }

    if (isGroupEntities) {
        columns.push({ id: LegalEntityColumnId.preselectedOnCreate, translation: translateString("preselectedOnCreate") });
    }

    if (hasTagsAccess) {
        columns.push({ id: LegalEntityColumnId.tags, translation: translateString("tag") });
    }

    // Absorbed from the custom list vocabulary. Single-value fields keep the propertyNames label their custom list
    // used, so a migrated view reads the same. The multi-value ones are headed in the singular instead, which
    // propertyNames cannot express: those entries are still the column headers of custom lists nobody has migrated
    // yet, and they also label this page's filters and the audit log.
    columns.push(
        { id: LegalEntityColumnId.description, translation: translatePropertyName("Description") },
        { id: LegalEntityColumnId.address, translation: translatePropertyName("Address") },
        { id: LegalEntityColumnId.zipCode, translation: translatePropertyName("ZipCode") },
        { id: LegalEntityColumnId.city, translation: translatePropertyName("City") },
        { id: LegalEntityColumnId.companyNumber, translation: translatePropertyName("CompanyNumber") },
        { id: LegalEntityColumnId.referenceNumber, translation: translatePropertyName("ReferenceNumber") },
        { id: LegalEntityColumnId.tiaNotNecessary, translation: translatePropertyName("TIANotNecessary") },
        { id: LegalEntityColumnId.groupCompany, translation: translatePropertyName("IsGroupCompany") },
        { id: LegalEntityColumnId.supervisionType, translation: translatePropertyName("LegalEntitySupervisionType") },
        { id: LegalEntityColumnId.supervisionFrequency, translation: translatePropertyName("LegalEntitySupervisionFrequency") },
        { id: LegalEntityColumnId.location, translation: translatePropertyName("Location") },
        { id: LegalEntityColumnId.transferBasis, translation: translatePropertyName("TransferBasis") },
        { id: LegalEntityColumnId.dataProcessor, translation: translatePropertyName("IsDataProcessor") },
        { id: LegalEntityColumnId.subProcessor, translation: translatePropertyName("IsSubProcessor") },
        { id: LegalEntityColumnId.recipientOnDisclosure, translation: translatePropertyName("IsCompanyRecipientOnDislosure") },
        { id: LegalEntityColumnId.senderOnSource, translation: translatePropertyName("IsCompanySenderOnSource") },
        { id: LegalEntityColumnId.senderOnDataProcessor, translation: translatePropertyName("IsCompanySenderOnDataProcessor") },
        { id: LegalEntityColumnId.documents, translation: translateString("document") },
        { id: LegalEntityColumnId.hasDocuments, translation: translatePropertyName("CompanyHasDocuments") },
        { id: LegalEntityColumnId.documentTypes, translation: translateString("documentType") },
        { id: LegalEntityColumnId.documentOwners, translation: translatePropertyName("DocumentOwners") },
        { id: LegalEntityColumnId.documentGroupEntities, translation: translateString("groupEntityOnDocuments") },
        {
            id: LegalEntityColumnId.contacts,
            translation: translatePropertyName("Contacts"),
            tooltip: translatePropertyName("ContactsCompanyToolTip"),
        },
        { id: LegalEntityColumnId.processingActivities, translation: translateString("processingActivity") },
        { id: LegalEntityColumnId.numberOfProcessingActivities, translation: translatePropertyName("NumberOfProcessingActivities") },
        { id: LegalEntityColumnId.dataCategories, translation: translateString("dataCategory") },
        { id: LegalEntityColumnId.numberOfDataCategories, translation: translatePropertyName("NumberOfDataCategories") },
        { id: LegalEntityColumnId.dataSubjects, translation: translateString("dataSubjectCategory") },
        { id: LegalEntityColumnId.numberOfDataSubjects, translation: translatePropertyName("NumberOfDataSubjectObjects") },
        { id: LegalEntityColumnId.roles, translation: translateString("role") },
        { id: LegalEntityColumnId.numberOfSharings, translation: translatePropertyName("NumberOfSharings") },
        // Fields the entity has that neither the old overview nor the old lists offered.
        { id: LegalEntityColumnId.note, translation: translateString("note") },
        { id: LegalEntityColumnId.website, translation: translateString("website") },
        // "secondarySupplier" is the existing singular of "subContractors" in all three languages, despite the name.
        { id: LegalEntityColumnId.subContractors, translation: translateString("secondarySupplier") },
        { id: LegalEntityColumnId.transferBasisNotNecessary, translation: translateString("transferBasisNotRequired") }
    );

    // Keyed by the bare field id, the way the system and contract column pickers key theirs, so a saved view's
    // display blob needs no prefix to tell a custom field from a built-in column.
    const customFields = (customInputFieldsQuery.data ?? [])
        .slice()
        .sort((a, b) => (a.name > b.name ? 1 : -1))
        .map((customField) => ({ id: customField.id, translation: customField.name, type: customField.type }));

    function getTableFieldsDictionary(): Record<string, LegalEntityFieldDefinition> {
        return Object.fromEntries(
            columns
                .map((column): [string, LegalEntityFieldDefinition] => [column.id, { translation: column.translation, tooltip: column.tooltip }])
                .concat(customFields.map((customField): [string, LegalEntityFieldDefinition] => [customField.id, { translation: customField.translation }]))
        );
    }

    const tableFields: Array<TableFields> = columns
        .map((column) => ({
            id: column.id,
            name: column.translation,
            grouping: legalEntityColumnGrouping(column.id),
        }))
        .concat(
            customFields.map((customField) => ({
                id: customField.id,
                name: customField.translation,
                grouping: LegalEntityFieldGrouping.CustomFields,
            }))
        );

    return { tableFields, getTableFieldsDictionary, hasTagsAccess, isLoading: customInputFieldsQuery.isLoading };
}
